/**
 * devOnlyGuard — `CanActivateFn` that blocks dev/tooling-only routes in a
 * production build (PRO-426).
 *
 * RESPONSIBILITY: surfaces like the admin Brand Test and Map Ingest harness
 * are engineering tools, not customer features. Rail visibility already
 * hides them, but a direct URL hit (`/admin/brand-test`, bookmark, paste)
 * would still resolve in a production bundle. Pair
 * `canActivate: [devOnlyGuard]` on those routes so a production visitor falls
 * through to Overview exactly as they would for any non-existent admin URL,
 * while dev/local builds keep the tools reachable.
 *
 * SEMANTICS:
 *   • Non-production (`!environment.production`) → always allowed.
 *   • Production → redirect to `/admin/overview` (never reveal the route by
 *     erroring differently from a normal miss).
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { environment } from '../../../environments/environment';

/**
 * The same rule, with a caller-chosen landing place.
 *
 * `/admin/overview` is the right fall-through for the admin tools this guard
 * was written for, and the wrong one for anything a signed-out visitor can
 * reach: they would land on an admin URL, be bounced again by `adminGuard`,
 * and arrive somewhere via two redirects instead of one. The `/preview/*`
 * harnesses are exactly that case — they are deliberately auth-free, so their
 * production fall-through has to be a route anyone can hold.
 */
export const devOnlyGuardTo =
  (redirectTo: string): CanActivateFn =>
  () => {
    if (!environment.production) return true;
    return inject(Router).createUrlTree([redirectTo]);
  };

export const devOnlyGuard: CanActivateFn = devOnlyGuardTo('/admin/overview');

/**
 * For the auth-free `/preview/*` review harnesses: same production block,
 * landing on the app root rather than an admin URL the visitor cannot hold.
 */
export const previewOnlyGuard: CanActivateFn = devOnlyGuardTo('/');
